// import {  AccumulativeShadows, RandomizedLight } from '@react-three/drei';
import { Vector3 } from 'three';
import { calcMag, mapClamp, pixelIndexToSphericalCoords } from '../utils/math3d';
import { AccumulativeShadows , RandomizedLight, SHADOW_MAP_SIZE } from './AccumulativeShadowsV2';

const calcShadowScale = (lightsPositionV3, minShadowScale = 2, maxShadowScale = 30) => {
  let shadowHalfScale = minShadowScale * 0.5;

  const lpV3 = new Vector3();
  const ltV3 = new Vector3();
  const shadowPosV3 = new Vector3();

  const setShadowHalfScaleIfSafe = (num) => {
    if (Number.isFinite(num) && (Math.abs(num) > shadowHalfScale)) {
      shadowHalfScale = Math.abs(num);
    }
  }
  const increaseScaleForPoint = (pointX, pointY, pointZ) => {
    lpV3.set(pointX, pointY, pointZ).sub(lightsPositionV3);
    ltV3.copy(lpV3).multiplyScalar(-lightsPositionV3.y / lpV3.y)
    shadowPosV3.copy(lightsPositionV3).add(ltV3);
    setShadowHalfScaleIfSafe(shadowPosV3.x);
    setShadowHalfScaleIfSafe(shadowPosV3.z);
  }

  // Check shadow scale on 4 top corners of bounding box
  increaseScaleForPoint( 0.5, 1, -0.5);
  increaseScaleForPoint( 0.5, 1,  0.5);
  increaseScaleForPoint(-0.5, 1, -0.5);
  increaseScaleForPoint(-0.5, 1,  0.5);

  return Math.min(maxShadowScale, shadowHalfScale * 2);
}

const calcShadowScaleSafe = (lightsPositionV3SafeY) => {
  const scaleBuffer = 1;
  const scale = calcShadowScale(lightsPositionV3SafeY) + scaleBuffer;
  return scale;
}

/**
 * 
 * @param {number[][]} hist
 * @param {number} minCount when reached will stop adding bins from `hist`
 * @returns {number[]}
 */
const getMostUsedIndicesFromHdri = (hist, minCount) => {
  let indices = [];

  for (let bin = hist.length - 1; (bin >= 0) && (indices.length < minCount); bin--) {
    indices.push(...hist[bin]);
  }

  return indices;
}

const calcShadowProps = (lightsPosition) => {
  const lightDistance = calcMag(lightsPosition);
  const lightsPositionY = lightsPosition[1];
  const cosA = lightsPositionY / lightDistance;
  const alphaTest = mapClamp(0.1, 0.4, 0.3, 0.75, cosA);

  // Don't let light position be lower than bounding box top.
  // Using value of 2 which is higher than bounding box height of 1.
  const safeLightPositionY = Math.max(2, lightsPositionY);
  const lightsPositionV3SafeY = new Vector3(lightsPosition[0], safeLightPositionY, lightsPosition[2]);
  const scale = calcShadowScaleSafe(lightsPositionV3SafeY);
  return {alphaTest, scale, lightsPositionV3SafeY};
}

const calcShadowPropsFromHdri = (hdriData, lightDistance) => {
  const v3 = new Vector3();
  const indices = getMostUsedIndicesFromHdri(hdriData.hist, 100);
  let res = null;

  for (const pixelIndex of indices) {
    const { phi, theta } = pixelIndexToSphericalCoords(pixelIndex, hdriData.width, hdriData.height);
    const pixelLightPosition = v3.setFromSphericalCoords(lightDistance, phi, theta).toArray();
    const {alphaTest, scale} = calcShadowProps(pixelLightPosition);

    if (res === null) {
      res = {alphaTest, scale};
    } else {
      res.alphaTest = Math.min(res.alphaTest, alphaTest);
      res.scale = Math.max(res.scale, scale);
    }
  }

  return res;
}

export const Shadows = ({ frames, lightsPosition, color, opacity , softness, hdriData}) => {
  const lightDistance = calcMag(lightsPosition);

  // Decrease alpha test so we won't see the entire plane darken
  const lightDeltaRadius = mapClamp(0, 1, 0.4, 5, softness);
  const ambient = mapClamp(0.2, 1, 0, 0.5, softness);

  const scaledOpacity = Math.pow(opacity, 2);

  let {alphaTest, scale, lightsPositionV3SafeY} = calcShadowProps(lightsPosition);
  if (hdriData?.hist) {
    const propsFromHdri = calcShadowPropsFromHdri(hdriData, lightDistance);
    if (propsFromHdri) {
      alphaTest = propsFromHdri.alphaTest;
      scale = propsFromHdri.scale;
    }
  }

  return (
  <AccumulativeShadows
    temporal={false}
    frames={frames}
    alphaTest={alphaTest}
    toneMapped={true}
    scale={scale}
    color={color}
    opacity={scaledOpacity}
  >
    <RandomizedLight
      amount={3}
      radius={lightDeltaRadius}
      ambient={ambient}
      size={1.1}
      bias={0.0001}
      mapSize={SHADOW_MAP_SIZE}
      position={lightsPositionV3SafeY.toArray()}
      minLightPositionY={3}
      hdriData={hdriData}
      />
    </AccumulativeShadows>
  )
};
